<template>
    <div v-if="trainerPosition" class="ll-root" :style="bgStyle">
        <div class="ll-content">
            <div class="ll-lose-card" :class="cardClass">
                <div class="ll-title">{{ resultTitle }}</div>
                <v-img
                    class="ll-lose-img"
                    :src="require(`@/assets/pic/web/${resultImg}`)"
                    contain
                />
                <div class="ll-subtitle">{{ resultSubtitle }}</div>
                <div
                    v-for="(line, index) in messageLines"
                    :key="index"
                    class="ll-message"
                >
                    {{ line }}
                </div>

                <div v-if="lastOpponent" class="ll-opponent-row">
                    <v-img
                        v-if="lastOpponent.img"
                        :src="require(`@/assets/pic/trainer/${lastOpponent.img}`)"
                        class="ll-opponent-img"
                        contain
                    />
                    <div class="ll-opponent-info">
                        <div>{{ $t('journey.leagueResult.roundLabel') }} {{ trainerPosition.league ? trainerPosition.league.round : '' }}</div>
                        <div v-if="fightEnvironmentName">{{ stadionLabel }}: {{ fightEnvironmentName }} {{ $t('journey.leagueResult.environmentStadionSuffix') }}</div>
                        <div>{{ opponentLabel }}: {{ lastOpponent.fullName }}</div>
                    </div>
                </div>

                <v-btn
                    large
                    rounded
                    block
                    :loading="isLoading"
                    :disabled="isLoading"
                    color="black--text"
                    class="btn-shadow ll-continue-btn"
                    :class="continueBtnClass"
                    @click="startNewYearOfJourney()"
                >
                    {{ $t('journey.leagueResult.continueBtn') }}
                </v-btn>
            </div>

            <div class="ll-toplist-card" :class="cardClass">
                <div class="ll-toplist-title">{{ $t('journey.leagueResult.toplistTitle') }}</div>
                <div class="ll-toplist-scroll">
                    <ToplistTable
                        :toplist="toplist"
                        :dark="true"
                        :rollable="true"
                        :headers="['', $t('journey.leagueResult.headerTrainer'), $t('journey.leagueResult.headerDay'), $t('journey.leagueResult.headerDate')]"
                        :keys="['index','trainer','day','created']"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    // Sdílená komponenta pro LEAGUE_WIN i LEAGUE_LOSE (stejný princip jako
    // DungeonWinNext.vue) - staré LeagueWin.vue/LeagueLose.vue zůstávají
    // zmrazené/referenční za !flags.newUi. Viz claude/components/journey-oak-league.md.
    //
    // journeyApi.js: `leagueToplists()` opravuje pre-existující bug - stará
    // LeagueLose.vue řadila žebříček přes `order[days]`, entita má ale pole `day`.
    import {mapActions, mapGetters} from 'vuex';
    import * as journeyApi from '@/api/journeyApi';
    import ToplistTable from '@/components/journey/positions/pieces/ToplistTable';

    export default {
        name: 'LeagueResultNext',
        components: {ToplistTable},
        props: {
            trainerPosition: {type: Object, default: null},
            isWin: {type: Boolean, default: false},
        },
        data() {
            return {
                toplist: [],
            }
        },
        computed: {
            ...mapGetters({
                isLoading: 'journey/isLoading',
                user: 'user/item',
            }),
            bgStyle() {
                return {backgroundImage: `url(${require('@/assets/pic/web/league_stadion.png')})`};
            },
            cardClass() {
                return this.isWin ? 'transparent07--dark' : 'transparent08--dark';
            },
            continueBtnClass() {
                return this.isWin ? 'secondary' : 'warning';
            },
            resultTitle() {
                return this.isWin ? this.$t('journey.leagueResult.winTitle') : this.$t('journey.leagueResult.loseTitle');
            },
            resultSubtitle() {
                return this.isWin ? this.$t('journey.leagueResult.winSubtitle') : this.$t('journey.leagueResult.loseSubtitle');
            },
            resultImg() {
                return this.isWin ? 'win.png' : 'lose.png';
            },
            stadionLabel() {
                return this.isWin ? this.$t('journey.leagueResult.finalStadionLabel') : this.$t('journey.leagueResult.fightAtLabel');
            },
            opponentLabel() {
                return this.isWin ? this.$t('journey.leagueResult.finalOpponentLabel') : this.$t('journey.leagueResult.lastOpponentLabel');
            },
            messageLines() {
                if (this.isWin) {
                    return [1, 2, 3, 4, 5, 6, 7, 8].map((n) => this.$t('journey.leagueResult.winMessages.line' + n));
                }
                return [this.$t('journey.leagueResult.loseMessage', {days: this.trainerPosition.days})];
            },
            lastOpponent() {
                return this.trainerPosition.trainerFight && this.trainerPosition.trainerFight.trainer;
            },
            fightEnvironmentName() {
                return this.trainerPosition.pokemonFight
                    && this.trainerPosition.pokemonFight.fightEnvironment
                    && this.trainerPosition.pokemonFight.fightEnvironment.name;
            },
        },
        created() {
            this.trainerInfoMessages(this.user.id);
            this.getToplist();
        },
        methods: {
            ...mapActions({
                fetchData: 'journey/fetchObject',
                positionPost: 'journey/positionPost',
                trainerInfoMessages: 'trainer/loadInfoMessages',
            }),
            startNewYearOfJourney() {
                this.positionPost(journeyApi.travel(this.trainerPosition.id, 1));
            },
            getToplist() {
                this.fetchData(journeyApi.leagueToplists()).then((data) => {
                    this.toplist = data['member'];
                });
            },
        },
    }
</script>

<style scoped>
.ll-root {
    position: fixed;
    inset: 0;
    height: 100%;
    background-size: cover;
    background-position: center;
    overflow-y: auto;
}
.ll-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    /* bottom 54px = rezerva pro globální TeamBottomSheet - .ll-root je "position: fixed; inset: 0" */
    padding: 74px 16px 54px 16px;
    box-sizing: border-box;
    min-height: 100%;
}
.ll-lose-card {
    width: 100%;
    max-width: 420px;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 18px;
    padding: 18px 16px 16px 16px;
    text-align: center;
    box-shadow: 2px 3px 10px rgba(0,0,0,0.35);
    box-sizing: border-box;
}
.ll-title {
    font-size: 24px;
    font-weight: 800;
    margin-bottom: 8px;
}
.ll-lose-img {
    max-width: 140px;
    margin-bottom: 8px;
}
.ll-subtitle {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 8px;
}
.ll-message {
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 10px;
}
.ll-opponent-row {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;
    text-align: left;
}
.ll-opponent-img {
    flex: 0 0 35%;
    max-width: 35%;
    max-height: 100px;
}
.ll-opponent-info {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 14px;
    font-weight: 600;
}
.ll-continue-btn {
    margin-top: 4px;
}
.ll-toplist-card {
    width: 100%;
    max-width: 420px;
    flex: 1 0 auto;
    min-height: 160px;
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    padding: 10px 10px 4px 10px;
    box-shadow: 2px 3px 8px rgba(0,0,0,0.3);
    box-sizing: border-box;
}
.ll-toplist-title {
    flex: none;
    text-align: center;
    font-size: 22px;
    font-weight: 800;
    margin-bottom: 4px;
}
.ll-toplist-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}
</style>
